/**
 * Step 4 of the Upload Wizard — read-only summary card with the seven
 * fields per `feature-spec.md` §2.5.
 *
 * The dimensions list comes from `collection.resolvedDimensions` (taxonomy
 * pinned at collection time); the "(all dimensions)" qualifier signals
 * that this is fixed by the collection, not user-chosen.
 */

import React from 'react';

import { Card, CardContent } from '../../../../ui/card';
import { CHANNELS, type ChannelKey } from '../../shared/channels';
import { useVersionList } from '../../hooks/useTaxonomy';
import type { Collection } from '../types';
import type { ParseResult } from './Step1UploadCsv';

interface Props {
  collection: Collection;
  file: File | null;
  parseResult: ParseResult | null;
  channelKey: ChannelKey;
  taxonomyVersionId: string;
}

const SummaryRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex flex-col gap-1 border-b border-border/60 py-3 last:border-0 md:flex-row md:items-baseline md:gap-4">
    <div className="w-40 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div className="flex-1 text-[13px] text-foreground">{children}</div>
  </div>
);

const Step4Review: React.FC<Props> = ({
  collection,
  file,
  parseResult,
  channelKey,
  taxonomyVersionId,
}) => {
  const { data: taxonomyVersions } = useVersionList(collection.taxonomyKey);

  const taxonomy = (taxonomyVersions ?? []).find((v) => v.id === taxonomyVersionId);
  const channelMeta = CHANNELS[channelKey];
  const sortedDimensions = [...collection.resolvedDimensions].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  return (
    <Card data-testid="step4-review">
      <CardContent className="p-5">
        <SummaryRow label="Collection">{collection.name}</SummaryRow>
        <SummaryRow label="File summary">
          {file && parseResult ? (
            <>
              {file.name} · {parseResult.totalCount.toLocaleString()} records
            </>
          ) : (
            <span className="text-muted-foreground">No file selected</span>
          )}
        </SummaryRow>
        <SummaryRow label="Channel">
          <span className="inline-flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: channelMeta.color }}
              aria-hidden="true"
            />
            {channelMeta.name}
          </span>
        </SummaryRow>
        <SummaryRow label="Annotate For">
          <div className="flex flex-wrap gap-1.5">
            {sortedDimensions.map((d) => (
              <span
                key={d.key}
                className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-foreground"
              >
                {d.name}
              </span>
            ))}
            <span className="text-[11px] italic text-muted-foreground">
              (all dimensions)
            </span>
          </div>
        </SummaryRow>
        <SummaryRow label="Taxonomy">
          {taxonomy ? `v${taxonomy.versionNumber}` : '—'}
        </SummaryRow>
        {/* B17-FE: set expectations about lenient row-by-row rejection
            before the user hits submit. The BE imports valid rows and
            reports rejections individually rather than failing the
            whole file for a few malformed rows. Gated by the same
            file+parseResult check the File summary uses. */}
        {file && parseResult && (
          <p className="mt-4 text-[11px] text-muted-foreground">
            Rejected rows will be reported after import — we won&apos;t
            reject the whole file for a few malformed rows.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default Step4Review;
